import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms'; // <-- Import FormControl
import { ReactiveFormsModule } from '@angular/forms'; // <-- Add this import

import { Attachment } from '../../objects/attachment';
import { Router, ActivatedRoute } from "@angular/router";
import { AttachmentListComponent } from '../../list-all-pages/attachment-list/attachment-list.component';

import { CreateEntity } from '../create-entity';
import { FormsModule } from '@angular/forms';

import { AgGridModule } from 'ag-grid-angular';

import { MatInputModule } from '@angular/material/input';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';

import { AutoCompleteModule } from 'primeng/autocomplete';
import { PartySheetsService } from '../../service/google-sheets/party-sheets.service';
import { NetSuiteSheetsService } from '../../service/google-sheets/netsuite-sheets.service';
import { firstValueFrom, Observable, Subscription, startWith, map, combineLatest } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { Party } from '../../objects/party';
import { EntityType } from '../../objects/interface-library';


import { PartyLabelPipe } from '../../shared/pipes/party-label.pipe';
@Component({
    selector: 'app-new-vendor-full-page',
    templateUrl: './new-vendor-full-page.component.html',
    styleUrls: ['./new-vendor-full-page.component.css'],
    standalone: true,
    imports: [
      PartyLabelPipe,
      AgGridModule,
      CommonModule,
      FormsModule,
      MatInputModule,
      MatGridListModule,
      MatCheckboxModule,
      MatExpansionModule,
      MatSlideToggleModule,
      MatSliderModule,
      MatOption,
      MatProgressSpinner,
      MatSelectModule,
      MatAutocompleteModule,
      MatFormFieldModule,
      MatIconModule,
      MatCardModule,
      MatButtonModule,
      AutoCompleteModule,
      ReactiveFormsModule
    ]
  })
export class NewVendorFullPageComponent extends CreateEntity implements OnInit {
  private partySheetsService = inject(PartySheetsService);
  private commonSheetsService = inject(CommonSheetsService);
  private netSuiteSheetsService = inject(NetSuiteSheetsService);

  // Create a FormGroup to manage all your form controls
  form: FormGroup;
  private formSubscription!: Subscription;
  
  currentParty!: Party;
  contractAttachment: Attachment;

  specialties: any[] = [];
  listOfAttachmentNames = [];
  stringOfAttachmentNames = null;

  partyTypes: any[] = [];
  partyStanding: any[] = [];
  geographicStates: any[] = [];
  diversityStandingOptions: string[] = [];
  licenseTypes: string[] = [];
  categories1099: string[] = [];
  businessStructures: string[] = [];
  paymentTermsOptions: string[] = [];

  saving = false;
  isEditMode = false;
  /** True when the loaded party originated from NetSuite (sourceSystem === 'netsuite').
   *  Such parties are read-only in HC: the form is disabled and save is blocked.
   *  Whole-record read-only for now; field-level authority can come later. */
  isNetsuiteSourced = false;

  // Parent Party autocomplete: alphabetized + filter-as-you-type. The control
  // `parentVendor` holds the selected vendorNum; `displayParentName` renders it
  // as the party name. `parentPartiesCache` is a synchronous sorted copy used by
  // displayWith (and kept fresh via parentPartiesSub).
  filteredParents$!: Observable<any[]>;
  private parentPartiesCache: any[] = [];
  private parentPartiesSub!: Subscription;

  // State autocomplete filtered observables
  filteredStates$!: Observable<string[]>;
  filteredSecondaryStates$!: Observable<string[]>;
  filteredW9States$!: Observable<string[]>;
  filteredRemittanceStates$!: Observable<string[]>;
  filteredStatesOfIncorporation$!: Observable<string[]>;

  okToLeaveIntakeForm = false;

  submitted = false;
  reviewer = false;
  viewPDF = false;
  isChild = false;
  isSubContractor = false;

  peopleAssociatedWithContractParty: any[] = [];


  constructor(public dialog: MatDialog,
              public router: Router,
              private route: ActivatedRoute,
              private fb: FormBuilder,
              private cdr: ChangeDetectorRef) {

      super();

      this.allUsers$ = this.usersService.allUsers$;

      this.contractAttachment = {};

      const datetimestamp = (new Date).getTime().toString();


      // Initialize the form with all your controls in one place
      this.form = this.fb.group({
        owner: new FormControl(''),
        vendorNum: [datetimestamp],
        name: [''],
        accountingCompanyName: [''],
        firstName: [''],
        lastName: [''],
        email: [''],
        dba: [''],
        parentVendor: [''],
        formallyKnownAs: [''],
        standing: [''],
        partyType: [''],
        website: [''],
        glNumber: [''],
        dunsNum: [''],
        npiNum: [''],
        ccnNum: [''],
        isSubContractor: [''],
        isChildOfParentParty: [''],
        address: [''],
        city: [''],
        state: [''],
        zip: [''],
        secondaryAddress: [''],
        secondaryCity: [''],
        secondaryState: [''],
        secondaryZip: [''],
        assessor: [''],
        finalReviewer: [''],
        phonePrimary: [''],
        phoneSecondary: [''],
        fax: [''],
        annualRevenues: [''],
        numEmployees: [''],
        stateOfIncorporation: [''],
        publicOrPrivate: [''],
        county: [''],
        country: [''],
        nonOrForProfit: [''],
        taxExemptNum: [''],
        tin: [''],
        specialty: [''],
        description: [''],
        baaRequired: [''],
        coiRequired: [''],
        // Tax and Compliance Fields
        de542: [false],
        diversityStanding: [[]],
        oigScreening: [false],
        licenseType: [''],
        licenseNumber: [''],
        licenseState: [''],
        category1099: [''],
        is1099Eligible: [false],
        ancillary: [false],
        fein: [''],
        glAccountNumber: [''],
        businessStructure: [''],
        // W9 Address Fields
        w9StreetAddress1: [''],
        w9StreetAddress2: [''],
        w9City: [''],
        w9State: [''],
        w9Zip: [''],
        // Invoice Remittance Address Fields
        remittanceStreetAddress1: [''],
        remittanceStreetAddress2: [''],
        remittanceCity: [''],
        remittanceState: [''],
        remittanceZip: [''],
        printCheckAs: [''],
        paymentTerms: [''],
      });



  }


  async ngOnInit() {
    this.saving = true; // Show loading overlay

    try {
      // Get vendorNum from route parameter
      const vendorNum = this.route.snapshot.paramMap.get('id');

      // Load static field values first
      await this.getStaticFieldValues();

      // Load autocomplete data. settledCache, not firstValueFrom — allUsers$ is a
      // BehaviorSubject seeded with [] and filled asynchronously, so grabbing the
      // first emission could hand back an empty list and leave the user pickers
      // permanently blank. settledCache waits for it to actually arrive.
      this.filteredUsers = await settledCache(this.allUsers$);

      // If we have a vendorNum, load the party (edit mode)
      if (vendorNum) {
        this.isEditMode = true;
        await this.loadPartyByNum(vendorNum);
      } else {
        // New party - initialize currentParty
        this.currentParty = {
          vendorNum: (new Date).getTime().toString()
        };
      }

      // Parent Party: alphabetize + filter-as-you-type (replaces the bare select).
      this.setupParentVendorAutocomplete();

      // Subscribe to the form's valueChanges once (after loading party data)
      this.formSubscription = this.form.valueChanges.subscribe(changes => {
        // This will be called whenever ANY control in the form changes
        // The `changes` object contains all the current values

        // Now you can update your currentParty object with the new values
        this.currentParty = { ...this.currentParty, ...changes };
      });
    } catch (error) {
      console.error('Error during initialization:', error);
    } finally {
      this.saving = false; // Hide loading overlay
      this.cdr.detectChanges(); // Trigger change detection to ensure spinner disappears
    }
  }

  ngOnDestroy() {
    // Don't forget to unsubscribe to prevent memory leaks
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.parentPartiesSub) {
      this.parentPartiesSub.unsubscribe();
    }
  }

  async loadPartyByNum(vendorNum: string) {
    try {
      // Get party from the allParties$ observable.
      //
      // settledCache, NOT firstValueFrom: allParties$ re-emits once per page of
      // the paginated fetch, so firstValueFrom saw roughly the first 100 of
      // cbhi's ~4,500 parties. Any party outside that early slice hit the
      // "Party not found" branch below — alert, then bounced back to the list —
      // making most parties simply un-editable.
      const parties = await settledCache(this.partyService.allParties$);
      const party = parties.find(p => p.vendorNum === vendorNum);

      if (party) {
        //console.log('Party found:', party);
        this.currentParty = party;
        this.populateFormWithParty(party);

        // NetSuite-sourced parties are read-only in HC (synced nightly). Disable the
        // whole form and flag the banner; onSubmit also hard-blocks as defense-in-depth.
        if (party.sourceSystem === 'netsuite') {
          this.isNetsuiteSourced = true;
          this.form.disable();
        }
      } else {
        console.error('Party not found with vendorNum:', vendorNum);
        alert('Party not found');
        this.router.navigate(['/securehome/parties']);
      }
    } catch (error) {
      console.error('Error loading party:', error);
      alert('Failed to load party');
    }
  }

  populateFormWithParty(party: Party) {
    //console.log('Populating form with party:', party);

    // Find user objects from the cached users list for autocomplete fields
    // Handle case where owner/assessor/finalReviewer might already be objects or strings
    const ownerEmail = typeof party.owner === 'object' ? (party.owner as any)?.email : party.owner;
    const assessorEmail = typeof party.assessor === 'object' ? (party.assessor as any)?.email : party.assessor;
    const finalReviewerEmail = typeof party.finalReviewer === 'object' ? (party.finalReviewer as any)?.email : party.finalReviewer;

    const ownerUser = this.filteredUsers?.find(u => u.email === ownerEmail);
    const assessorUser = this.filteredUsers?.find(u => u.email === assessorEmail);
    const finalReviewerUser = this.filteredUsers?.find(u => u.email === finalReviewerEmail);

    // Patch the form with party data
    this.form.patchValue({
      owner: ownerUser || party.owner,
      vendorNum: party.vendorNum,
      name: party.name,
      accountingCompanyName: party.accountingCompanyName,
      firstName: party.firstName,
      lastName: party.lastName,
      email: party.email,
      dba: party.dba,
      parentVendor: party.parentVendor,
      formallyKnownAs: party.formallyKnownAs,
      standing: party.standing,
      partyType: party.partyType,
      website: party.website,
      glNumber: party.glNumber,
      dunsNum: party.dunsNum,
      npiNum: party.npiNum,
      ccnNum: party.ccnNum,
      address: party.address,
      city: party.city,
      state: party.state,
      zip: party.zip,
      secondaryAddress: party.secondaryAddress,
      secondaryCity: party.secondaryCity,
      secondaryState: party.secondaryState,
      secondaryZip: party.secondaryZip,
      assessor: assessorUser || party.assessor,
      finalReviewer: finalReviewerUser || party.finalReviewer,
      phonePrimary: party.phonePrimary,
      phoneSecondary: party.phoneSecondary,
      fax: party.fax,
      annualRevenues: party.annualRevenues,
      numEmployees: party.numEmployees,
      stateOfIncorporation: party.stateOfIncorporation,
      publicOrPrivate: party.publicOrPrivate,
      county: party.county,
      country: party.country,
      nonOrForProfit: party.nonOrForProfit,
      taxExemptNum: party.taxExemptNum,
      tin: party.tin,
      specialty: party.specialty,
      description: party.description,
      baaRequired: party.baaRequired,
      coiRequired: party.coiRequired,
      // Tax and Compliance Fields
      de542: party.de542 || false,
      diversityStanding: Array.isArray(party.diversityStanding) ? party.diversityStanding : (party.diversityStanding ? [party.diversityStanding] : []),
      oigScreening: party.oigScreening || false,
      licenseType: party.licenseType,
      licenseNumber: party.licenseNumber,
      licenseState: party.licenseState,
      category1099: party.category1099,
      is1099Eligible: party.is1099Eligible || false,
      ancillary: party.ancillary || false,
      fein: party.fein,
      glAccountNumber: party.glAccountNumber,
      businessStructure: party.businessStructure,
      // W9 Address Fields
      w9StreetAddress1: party.w9StreetAddress1,
      w9StreetAddress2: party.w9StreetAddress2,
      w9City: party.w9City,
      w9State: party.w9State,
      w9Zip: party.w9Zip,
      // Invoice Remittance Address Fields
      remittanceStreetAddress1: party.remittanceStreetAddress1,
      remittanceStreetAddress2: party.remittanceStreetAddress2,
      remittanceCity: party.remittanceCity,
      remittanceState: party.remittanceState,
      remittanceZip: party.remittanceZip,
      printCheckAs: party.printCheckAs,
      paymentTerms: party.paymentTerms,
    });
  }

  displayUserFn(user: any): string {
    return user && user.shortFriendlyName ? user.shortFriendlyName : '';
  }

  parentVendorChanged(event) {
  }

  /**
   * Wire the Parent Party autocomplete: the option list is alphabetized by name
   * and narrows as the user types (fixing the old bare mat-select, which only
   * jumped to the next option starting with the typed letters). Reacts to both
   * the parties feed and the control's own value.
   */
  private setupParentVendorAutocomplete(): void {
    const ctrl = this.form.get('parentVendor');
    if (!ctrl) return;

    // Keep a synchronous, alphabetized copy for displayWith (vendorNum → name).
    this.parentPartiesSub = this.allParties$.subscribe(list => {
      this.parentPartiesCache = this.sortByName(list);
      // Edit mode: parentVendor may have been patched with a vendorNum BEFORE the
      // parties list arrived, leaving displayWith showing the raw id. Once the
      // names are available, re-render the display — but ONLY when the control
      // holds a resolved vendorNum (never while the user is typing a search).
      if (ctrl.value && this.parentPartiesCache.some(p => String(p.vendorNum) === String(ctrl.value))) {
        ctrl.setValue(ctrl.value, { emitEvent: false });
      }
    });

    this.filteredParents$ = combineLatest([
      this.allParties$,
      ctrl.valueChanges.pipe(startWith(ctrl.value ?? ''))
    ]).pipe(
      map(([list, value]) => this.filterParents(list, value))
    );
  }

  private sortByName(list: any[]): any[] {
    return [...(list || [])].sort((a, b) =>
      (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' })
    );
  }

  private filterParents(list: any[], value: any): any[] {
    const sorted = this.sortByName(list);
    const v = (value ?? '').toString().trim().toLowerCase();
    if (!v) return sorted;
    // After a selection the control holds a vendorNum, not a search string — show
    // the full list rather than filtering it down to nothing.
    if (sorted.some(p => String(p.vendorNum) === String(value))) return sorted;
    return sorted.filter(p => (p?.name || '').toLowerCase().includes(v));
  }

  /**
   * On blur, if the field holds typed text that was never resolved to a real
   * party (no matching vendorNum), clear it — otherwise save would persist a
   * bogus parentVendor string. A genuine selection sets the vendorNum first, so
   * it survives this check.
   */
  onParentVendorBlur(): void {
    const ctrl = this.form.get('parentVendor');
    if (!ctrl || !ctrl.value) return;
    const isResolved = this.parentPartiesCache.some(p => String(p.vendorNum) === String(ctrl.value));
    if (!isResolved) {
      ctrl.setValue('', { emitEvent: true });
    }
  }

  /** displayWith: render the stored vendorNum as its party name. */
  displayParentName = (vendorNum: string): string => {
    if (!vendorNum) return '';
    const match = this.parentPartiesCache.find(p => String(p.vendorNum) === String(vendorNum));
    return match?.name ?? vendorNum;
  };


  async getStaticFieldValues() {

    this.partyTypes = await firstValueFrom(this.partySheetsService.getPartyTypes());

    this.partyStanding = await firstValueFrom(this.partySheetsService.getPartyStanding());

    this.geographicStates = await firstValueFrom(this.commonSheetsService.getGeographicStates());

    this.specialties = await firstValueFrom(this.partySheetsService.getPartySpecialties());

    // Load NetSuite integration dropdown values from netsuite sheet
    this.diversityStandingOptions = await firstValueFrom(this.netSuiteSheetsService.getDiversityStanding());
    this.categories1099 = await firstValueFrom(this.netSuiteSheetsService.get1099Categories());
    this.businessStructures = await firstValueFrom(this.netSuiteSheetsService.getBusinessStructures());

    // Setup state autocomplete after loading states
    this.setupStateAutocomplete();
  }

  private setupStateAutocomplete(): void {
    // Primary state
    const stateControl = this.form.get('state');
    if (stateControl) {
      this.filteredStates$ = stateControl.valueChanges.pipe(
        startWith(stateControl.value || ''),
        map(value => this.filterStates(value || ''))
      );
    }

    // Secondary state
    const secondaryStateControl = this.form.get('secondaryState');
    if (secondaryStateControl) {
      this.filteredSecondaryStates$ = secondaryStateControl.valueChanges.pipe(
        startWith(secondaryStateControl.value || ''),
        map(value => this.filterStates(value || ''))
      );
    }

    // W9 state
    const w9StateControl = this.form.get('w9State');
    if (w9StateControl) {
      this.filteredW9States$ = w9StateControl.valueChanges.pipe(
        startWith(w9StateControl.value || ''),
        map(value => this.filterStates(value || ''))
      );
    }

    // Remittance state
    const remittanceStateControl = this.form.get('remittanceState');
    if (remittanceStateControl) {
      this.filteredRemittanceStates$ = remittanceStateControl.valueChanges.pipe(
        startWith(remittanceStateControl.value || ''),
        map(value => this.filterStates(value || ''))
      );
    }

    // State of incorporation
    const stateOfIncorporationControl = this.form.get('stateOfIncorporation');
    if (stateOfIncorporationControl) {
      this.filteredStatesOfIncorporation$ = stateOfIncorporationControl.valueChanges.pipe(
        startWith(stateOfIncorporationControl.value || ''),
        map(value => this.filterStates(value || ''))
      );
    }
  }

  private filterStates(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.geographicStates.filter(state =>
      state.toLowerCase().includes(filterValue)
    );
  }





  openAllAttachmentsInDialog(): void {

    const dialogRef = this.dialog.open(AttachmentListComponent, {
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      // this.currentParty.attachments.push(result.attachmentNum);
      // this.listOfAttachmentNames.push(result.name);
      // this.stringOfAttachmentNames = listify(this.listOfAttachmentNames);
    });
  }



  cancelSubmit() {
    this.okToLeaveIntakeForm = true;
    this.router.navigateByUrl('/securehome/parties');
  }

  /**
   * Checks for circular parent references.
   * Returns an error message if circular reference detected, null otherwise.
   */
  private async checkForCircularParentReference(partyToSave: Party): Promise<string | null> {
    const parentVendorNum = partyToSave.parentVendor;

    // No parent selected - no circular reference possible
    if (!parentVendorNum) {
      return null;
    }

    // Can't be your own parent
    if (parentVendorNum === partyToSave.vendorNum) {
      return 'A party cannot be its own parent.';
    }

    // Check if the selected parent has THIS party as its parent (direct circular reference).
    //
    // settledCache, not firstValueFrom: this is a validator, so a partial cache
    // fails OPEN — `find` returns undefined, every check below is skipped, and a
    // genuinely circular parent chain saves without complaint. It has to read the
    // complete list to be worth anything.
    const allParties = await settledCache(this.partyService.allParties$);
    const selectedParent = allParties.find(p => p.vendorNum === parentVendorNum);

    if (selectedParent && selectedParent.parentVendor === partyToSave.vendorNum) {
      return `Circular reference detected: "${selectedParent.name}" already has this party as its parent. Please remove the parent relationship from one of the parties first.`;
    }

    // Check for deeper circular references (A → B → C → A)
    const visited = new Set<string>();
    let currentParentNum: string | undefined = parentVendorNum;

    while (currentParentNum) {
      if (visited.has(currentParentNum)) {
        return 'Circular reference detected in the parent chain. Please check the parent relationships.';
      }
      if (currentParentNum === partyToSave.vendorNum) {
        return 'Circular reference detected: This party appears in its own ancestor chain.';
      }
      visited.add(currentParentNum);

      const currentParent = allParties.find(p => p.vendorNum === currentParentNum);
      currentParentNum = currentParent?.parentVendor;
    }

    return null;
  }

  async onSubmit() {

    // NetSuite-sourced parties are read-only in HC; never persist edits to them.
    if (this.isNetsuiteSourced) {
      alert('This party is sourced from NetSuite and is read-only in Healthy Contracts. Edits must be made in NetSuite.');
      return;
    }

    this.saving = true;

    const partyToSave: Party = this.form.value;

    //console.log('OBJECT TO SAVE: ', partyToSave);

    // Validate: prevent circular parent references
    const circularError = await this.checkForCircularParentReference(partyToSave);
    if (circularError) {
      alert(circularError);
      this.saving = false;
      return;
    }

    this.okToLeaveIntakeForm = true;

    //   // Call the service method and subscribe to the Observable
    this.partyService.writeParty(partyToSave).subscribe({
      next: (response) => {
        // This block runs on success
        //console.log('API call successful!', response);
        // You can now navigate or show a success message
        this.router.navigateByUrl('/securehome/parties');
      },
      error: (err) => {
        // This block runs on error
        console.error('An error occurred:', err);
        // Show an error message to the user
        // Example: this.snackBar.open('Failed to save party', 'Close');
      },
      complete: () => {
      }
    });

    // this.router.navigateByUrl('/securehome/wait');

    // if (this.currentParty.parentVendor) {
    //   await this.updateParentParty();
    // }

    this.saving = false;


    // this.router.navigate(['/securehome/vendor', this.currentParty.vendorNum]);
  }

}
